import React from "react";
import { CheckCircle2, LogIn, Save } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { Button } from "@/components/ui/button";

export default function ResultSavePanel({
  accountLoading,
  isAuthenticated,
  hasPendingClaim,
  saved,
  saving,
  error,
  onSave,
  onSignIn,
  onRegister,
}) {
  const { t } = useLang();

  if (saved) {
    return (
      <section
        className="mb-8 flex items-start gap-3 rounded-2xl border border-sage-200 bg-sage-50 p-5 text-teal-900"
        data-testid="result-saved-state"
        role="status"
      >
        <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-teal-700" />
        <div>
          <h2 className="font-display text-lg">{t("account.save.saved")}</h2>
          <p className="mt-1 text-sm text-teal-800/80">{t("account.save.saved_detail")}</p>
        </div>
      </section>
    );
  }

  if (!hasPendingClaim) return null;

  return (
    <section className="mb-8 rounded-2xl border border-terracotta-200 bg-terracotta-50/70 p-5 sm:p-6" data-testid="result-save-panel">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <h2 className="font-display text-xl text-teal-900">{t("account.save.title")}</h2>
          <p className="mt-1 max-w-2xl text-sm leading-relaxed text-muted-foreground">{t("account.save.description")}</p>
          <p className="mt-2 text-xs text-muted-foreground">{t("account.save.not_premium")}</p>
        </div>
        <div className="flex shrink-0 flex-wrap gap-2">
          {isAuthenticated ? (
            <Button
              type="button"
              onClick={onSave}
              disabled={saving || accountLoading}
              className="h-11 rounded-full bg-teal-800 px-5 text-cream-50 hover:bg-teal-700"
              data-testid="result-save-button"
            >
              <Save className="h-4 w-4" />
              {saving ? t("account.save.saving") : t("account.save.cta")}
            </Button>
          ) : (
            <>
              <Button
                type="button"
                onClick={onSignIn}
                disabled={accountLoading}
                className="h-11 rounded-full bg-teal-800 px-5 text-cream-50 hover:bg-teal-700"
                data-testid="result-save-signin"
              >
                <LogIn className="h-4 w-4" /> {t("account.signin")}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={onRegister}
                disabled={accountLoading}
                className="h-11 rounded-full px-5"
                data-testid="result-save-register"
              >
                {t("account.register")}
              </Button>
            </>
          )}
        </div>
      </div>
      {error && (
        <p className="mt-4 text-sm text-destructive" role="alert" data-testid="result-save-error">
          {error}
        </p>
      )}
    </section>
  );
}
